import React from "react";

const buildStringFragment = (str: string): React.ReactNode => {
    if (!str) {
        return null;
    }
    return <React.Fragment key={str}>{str}</React.Fragment>;
};

const prepareFormatString = (
    fmtString: string,
    data: {
        [key: string]: (
            content: string | React.ReactNode,
            index: number,
        ) => React.ReactNode;
    },
): { preparedFmtString: string; components: React.ReactNode[] } => {
    const components: React.ReactNode[] = [];
    const preparedFmtString = Object.keys(data).reduce<string>((memo, key) => {
        const tag = new RegExp(`<${key}>([^<]*)</${key}>`, "g");
        return memo.replace(tag, (_, content: string) => {
            const i = components.length;
            const component = data[key](content, i);
            components.push(component);
            return `<${i}>`;
        });
    }, fmtString);

    return { preparedFmtString, components };
};

const buildFragments = (
    preparedFmtString: string,
    components: React.ReactNode[],
): React.ReactNode[] => {
    let remainingFmtString = preparedFmtString;
    const fragments = components.reduce<React.ReactNode[]>(
        (memo, component, index) => {
            const [strBefore, strAfter] = remainingFmtString.split(
                `<${index}>`,
                2,
            );
            memo.push(buildStringFragment(strBefore));
            memo.push(component);
            remainingFmtString = strAfter;
            return memo;
        },
        [],
    );
    fragments.push(buildStringFragment(remainingFmtString));
    return fragments;
};

export const Trans = (props: {
    fmtString: string | null;
    data: {
        [key: string]: (
            content: string | React.ReactNode,
            index: number,
        ) => React.ReactNode;
    };
}): React.ReactNode => {
    if (!props.fmtString) {
        return null;
    }

    // Render the React components references in the format string, replacing
    // their references with index markers. e.g. Starting with:
    //
    //    This is a translated string <MyLink>with a link</MyLink> in it.
    //
    // And converts it to this:
    //
    //    This is a translated string <0> in it.
    //
    // …while also rendering the React component for <MyLink> and saving it
    // into an array.
    const { preparedFmtString, components } = prepareFormatString(
        props.fmtString,
        props.data,
    );

    // Now that we have a prepared format string that looks like this:
    //
    //    This is a translated string <0> in it.
    //
    // Convert it into an array of React Fragments, interlaced with the
    // previously rendered React components.
    const fragments = buildFragments(preparedFmtString, components);

    // Return the full rendered translated string
    return <>{fragments}</>;
};
